import { useMemo } from 'react';

import { STATUS_FILTERS, TYPE_FILTERS, DATE_FILTERS, type LiveTrainingSession } from './useLiveTrainingFilters';

interface FilterOptions {
  selectedStatusFilters: string[];
  selectedTypeFilters: string[];
  selectedDateFilters: string[];
}

interface FilteredSessionsResult {
  upcomingSessions: LiveTrainingSession[];
  pastSessions: LiveTrainingSession[];
  showUpcoming: boolean;
  showPast: boolean;
}

/**
 * Filters and sorts live training sessions based on selected filters
 */
export const useFilteredLiveTrainingSessions = (
  liveTrainingSessions: LiveTrainingSession[],
  { selectedStatusFilters, selectedTypeFilters, selectedDateFilters }: FilterOptions,
): FilteredSessionsResult => {
  return useMemo(() => {
    const now = new Date();

    // Determine which sections to show based on date filter
    // If no date filters are selected, show all sections
    const showUpcoming = selectedDateFilters.length === 0 || selectedDateFilters.includes(DATE_FILTERS.UPCOMING);
    const showPast = selectedDateFilters.length === 0 || selectedDateFilters.includes(DATE_FILTERS.PAST);

    // Split by time (upcoming vs past based on endDateTime)
    const upcoming = liveTrainingSessions.filter((session) => new Date(session.endDateTime) >= now);
    const past = liveTrainingSessions.filter((session) => new Date(session.endDateTime) < now);

    // Apply filters to both lists
    const filterSessions = (sessions: LiveTrainingSession[]): LiveTrainingSession[] => {
      let filtered = sessions;

      // Status filter - if any status filters are selected, apply them
      if (selectedStatusFilters.length > 0) {
        filtered = filtered.filter((s) => {
          const matchesEnrolled =
            selectedStatusFilters.includes(STATUS_FILTERS.ENROLLED) && s.enrolled && s.status === 'upcoming';
          const matchesCompleted = selectedStatusFilters.includes(STATUS_FILTERS.COMPLETED) && s.status === 'completed';
          const matchesSaved = selectedStatusFilters.includes(STATUS_FILTERS.SAVED) && s.isSaved;

          return matchesEnrolled || matchesCompleted || matchesSaved;
        });
      }

      // Type filter - if any type filters are selected, apply them
      if (selectedTypeFilters.length > 0) {
        filtered = filtered.filter((s) => {
          const matchesPublic = selectedTypeFilters.includes(TYPE_FILTERS.PUBLIC) && !s.isPrivate;
          const matchesPrivate = selectedTypeFilters.includes(TYPE_FILTERS.PRIVATE) && s.isPrivate;

          return matchesPublic || matchesPrivate;
        });
      }

      return filtered;
    };

    // Sort both lists by startDateTime (sooner first)
    const sortSessions = (sessions: LiveTrainingSession[]): LiveTrainingSession[] => {
      return [...sessions].sort((a, b) => {
        const dateA = new Date(a.startDateTime).getTime();
        const dateB = new Date(b.startDateTime).getTime();

        return dateA - dateB;
      });
    };

    return {
      upcomingSessions: sortSessions(filterSessions(upcoming)),
      pastSessions: sortSessions(filterSessions(past)),
      showUpcoming,
      showPast,
    };
  }, [liveTrainingSessions, selectedStatusFilters, selectedTypeFilters, selectedDateFilters]);
};
